import React, { useEffect, useState, useCallback, useRef } from 'react';
import { School, Plus, Trash2, Layers, ChevronRight, Upload, ClipboardList, Printer, FileText, Download, Loader2, Search } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useOrgId } from '../../hooks/useOrgId';
import { useAcademicYear } from '../../context/AcademicYearContext';
import { useT } from '../../i18n/LanguageContext';
import { schoolApi } from '../../api/school';
import { PageHeader, EmptyState, LoadingState, Card } from '../../components/school/ui';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BULLETIN_FORMATS = [
  { value: 'general_secondary', label: 'General / Secondary (FR)' },
  { value: 'primary_fr', label: 'Primary — French' },
  { value: 'primary_pt', label: 'Primary — Portuguese' },
];

export default function ClassesSectionsPage() {
  const { user, currentOrg } = useAuth();
  const { t } = useT();
  const orgId = useOrgId();
  const { selectedYearId } = useAcademicYear();
  const fileRef = useRef(null);
  const [classes, setClasses] = useState([]);
  const [search, setSearch] = useState('');
  const [sectionsByClass, setSectionsByClass] = useState({});
  const [teachers, setTeachers] = useState([]);
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [classDialog, setClassDialog] = useState(false);
  const [classForm, setClassForm] = useState({ name: '', numeric_level: '', bulletin_format: '' });
  const [sectionDialog, setSectionDialog] = useState(null); // class object
  const [sectionForm, setSectionForm] = useState({ name: '', capacity: 40, class_teacher_id: '' });
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  // Class list (roster) viewer
  const [rosterCtx, setRosterCtx] = useState(null); // { cls, section }
  const [rosterStudents, setRosterStudents] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);

  const loadAll = useCallback(async () => {
    if (!orgId) { setLoading(false); return; }
    try {
      setLoading(true);
      const [cls, tch, yrs] = await Promise.all([
        schoolApi.listClasses(orgId, selectedYearId), schoolApi.listTeachers(orgId), schoolApi.listYears(orgId),
      ]);
      setClasses(cls); setTeachers(tch); setYears(yrs || []);
      const map = {};
      await Promise.all(cls.map(async (c) => { map[c.id] = await schoolApi.listSections(orgId, c.id, selectedYearId); }));
      setSectionsByClass(map);
    } catch (e) { toast.error('Failed to load classes'); }
    finally { setLoading(false); }
  }, [orgId, selectedYearId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const activeYear = years.find((y) => y.is_active) || years[0];

  // Client-side filter: match by class name (case-insensitive substring)
  const q = search.trim().toLowerCase();
  const filteredClasses = q ? classes.filter((c) => (c.name || '').toLowerCase().includes(q)) : classes;

  // Open the class list (roster) for a given class + section
  const openRoster = async (cls, section) => {
    setRosterCtx({ cls, section });
    setRosterStudents([]);
    setRosterLoading(true);
    try {
      const res = await schoolApi.listStudents(orgId, {
        class_id: cls.id, section_id: section.id, academic_year_id: selectedYearId,
        sort_by: 'name', sort_dir: 'asc', limit: 10000,
      });
      setRosterStudents(res.students || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load class list');
    } finally {
      setRosterLoading(false);
    }
  };

  // Build the printable HTML sheet for the roster (used by both the Print and PDF
  // buttons). Matches the official ELI class-list layout (Portuguese): government +
  // school header, "Ano Escolar", class/turma line, a bordered Nº / Nome do Aluno /
  // Observação table, and the "Direção da ELI" footer note. Portrait A4.
  const buildRosterHtml = () => {
    if (!rosterCtx) return '';
    const esc = (v) => String(v == null ? '' : v).replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
    const schoolName = currentOrg?.name || 'Escola Libanesa Internacional - ELI';
    const logoUrl = currentOrg?.logo_url
      ? (currentOrg.logo_url.startsWith('http') ? currentOrg.logo_url : `${process.env.REACT_APP_BACKEND_URL}${currentOrg.logo_url}`)
      : '';
    const coaUrl = `${window.location.origin}/gb-coat-of-arms.png`;
    const yearName = (years.find((y) => y.id === selectedYearId) || activeYear || {}).name || '';
    const className = (rosterCtx.cls?.name || '').toUpperCase();
    const sectionName = (rosterCtx.section?.name || '').toUpperCase();
    const turma = `${className}${sectionName ? ' ' + sectionName : ''}`;
    const rows = rosterStudents.map((s, i) => (
      `<tr><td class="num">${i + 1}</td><td class="nome">${esc((s.name || '').toUpperCase())}</td><td class="obs"></td></tr>`
    )).join('');
    return `<!DOCTYPE html><html lang="pt"><head><meta charset="utf-8"/>
      <title>Lista de Alunos - ${esc(turma)}</title>
      <style>
        @page { size: A4 portrait; margin: 14mm 16mm; }
        * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        body { font-family: Arial, Helvetica, sans-serif; color: #000; margin: 0; }
        .hdr { position: relative; text-align: center; padding: 0 8px 12px 104px; margin-bottom: 10px; min-height: 100px; }
        .hdr .logo { position: absolute; left: 0; top: 0; height: 92px; width: auto; object-fit: contain; }
        .hdr .gov1 { font-size: 18px; line-height: 1.2; }
        .hdr .gov1 .coa { height: 26px; width: auto; vertical-align: middle; margin: 0 8px; }
        .hdr .gov2 { font-size: 14px; font-weight: 700; margin-top: 3px; }
        .hdr .school { font-size: 26px; font-weight: 800; margin-top: 12px; }
        .hdr .year { font-size: 20px; font-weight: 800; margin-top: 5px; }
        .hdr .year span { font-size: 17px; }
        .hdr .turma { font-size: 16px; font-weight: 800; margin-top: 8px; }
        table { width: 100%; border-collapse: collapse; margin-top: 4px; font-size: 13px; }
        th, td { border: 1px solid #000; padding: 6px 9px; }
        thead { display: table-header-group; }
        th { font-weight: 700; text-align: center; }
        th.nome, td.nome { text-align: left; }
        td.num, th.num { width: 44px; text-align: center; }
        td.obs, th.obs { width: 28%; }
        tr { page-break-inside: avoid; }
        .ft { margin-top: 28px; text-align: center; }
        .ft .line { width: 42%; margin: 0 auto 4px; border-top: 1px solid #000; }
        .ft .dir { font-weight: 700; font-size: 13px; }
        .ft .note { margin-top: 18px; font-size: 11px; text-align: justify; line-height: 1.45; }
        .ft .note b { font-weight: 700; }
      </style></head>
      <body>
        <div class="hdr">
          ${logoUrl ? `<img class="logo" src="${logoUrl}" alt="logo"/>` : ''}
          <div class="gov1">República da <img class="coa" src="${coaUrl}" alt="coat of arms"/> Guiné-Bissau</div>
          <div class="gov2">Ministério de Educação Nacional e do Ensino Superior</div>
          <div class="school">${esc(schoolName)}</div>
          <div class="year">Ano Escolar – <span>${esc(yearName) || '—'}</span></div>
          <div class="turma">${esc(turma)}</div>
        </div>
        <table>
          <thead><tr><th class="num">Nº</th><th class="nome">Nome do Aluno</th><th class="obs">Observação</th></tr></thead>
          <tbody>${rows || '<tr><td colspan="3" style="text-align:center">Sem alunos</td></tr>'}</tbody>
        </table>
        <div class="ft">
          <div class="line"></div>
          <div class="dir">Direção da ELI</div>
          <div class="note"><b>Observação:</b> Não é permitido a mudança da turma do aluno sem uma comunicação prévia à Direção Pedagógica. Mantenha obrigatoriamente o aluno à lista/turma da qual consta o seu nome.</div>
        </div>
      </body></html>`;
  };

  const printRoster = () => {
    const html = buildRosterHtml();
    const w = window.open('', '_blank');
    if (!w) { toast.error('Pop-up blocked. Please allow pop-ups to print.'); return; }
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  const exportRosterCsv = () => {
    if (!rosterCtx) return;
    const yearName = activeYear?.name || '';
    const className = rosterCtx.cls?.name || '';
    const sectionName = rosterCtx.section?.name || '';
    const header = ['#', 'Full Name', 'Academic Year', 'Class', 'Section'];
    const lines = rosterStudents.map((s, i) => [i + 1, `"${(s.name || '').replace(/"/g, '""')}"`, yearName, className, sectionName].join(','));
    const csv = [header.join(','), ...lines, '', `Total Students,${rosterStudents.length}`].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `class_list_${className}_${sectionName}.csv`.replace(/\s+/g, '_');
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const saveClass = async () => {
    if (!classForm.name.trim()) { toast.error(t('common.required')); return; }
    setSaving(true);
    try {
      await schoolApi.createClass({ organization_id: orgId, academic_year_id: selectedYearId, name: classForm.name, numeric_level: classForm.numeric_level ? Number(classForm.numeric_level) : null, bulletin_format: classForm.bulletin_format || null });
      toast.success(t('common.saved')); setClassDialog(false); setClassForm({ name: '', numeric_level: '', bulletin_format: '' }); loadAll();
    } catch (e) { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const setClassFormat = async (classId, value) => {
    try {
      await schoolApi.updateClass(classId, { bulletin_format: value });
      setClasses((prev) => prev.map((c) => (c.id === classId ? { ...c, bulletin_format: value } : c)));
      toast.success(t('common.saved', 'Saved'));
    } catch (e) { toast.error('Failed'); }
  };

  const saveSection = async () => {
    if (!sectionForm.name.trim()) { toast.error(t('common.required')); return; }
    setSaving(true);
    try {
      await schoolApi.createSection({ organization_id: orgId, academic_year_id: selectedYearId, class_id: sectionDialog.id, name: sectionForm.name, capacity: Number(sectionForm.capacity) || 40, class_teacher_id: sectionForm.class_teacher_id || null });
      toast.success(t('common.saved')); setSectionDialog(null); setSectionForm({ name: '', capacity: 40, class_teacher_id: '' }); loadAll();
    } catch (e) { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const delClass = async (id) => { try { await schoolApi.deleteClass(id); toast.success(t('common.deleted')); loadAll(); } catch (e) { toast.error('Failed'); } };
  const delSection = async (id) => { try { await schoolApi.deleteSection(id); toast.success(t('common.deleted')); loadAll(); } catch (e) { toast.error('Failed'); } };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    setImportPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/school/classes/import-excel/preview?organization_id=${orgId}`, fd);
      setImportPreview(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to preview file');
      setImportFile(null);
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const runImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const r = await axios.post(`${API}/school/classes/import-excel?organization_id=${orgId}`, fd);
      toast.success(r.data.message || 'Import complete');
      setImportOpen(false); setImportPreview(null); setImportFile(null);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto">
      <PageHeader title={t('classes.title')} subtitle={t('classes.subtitle')} icon={School}
        actions={(
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setImportOpen(true)} className="gap-2" data-testid="import-classes-button"><Upload className="w-4 h-4" /> {t('common.import', 'Import')}</Button>
            <Button onClick={() => setClassDialog(true)} className="gap-2" data-testid="add-class-button"><Plus className="w-4 h-4" /> {t('classes.newClass')}</Button>
          </div>
        )} />

      {loading ? <LoadingState rows={5} /> : classes.length === 0 ? (
        <Card><EmptyState icon={School} title={t('common.noData')} description={t('classes.subtitle')} actionLabel={t('classes.newClass')} onAction={() => setClassDialog(true)} /></Card>
      ) : (
        <>
          <Card className="p-4 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t('classes.search', 'Search classes...')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="classes-search-input"
              />
            </div>
          </Card>

          {filteredClasses.length === 0 ? (
            <Card>
              <EmptyState
                icon={Search}
                title={t('classes.noMatch', 'No classes match your search')}
                description={t('classes.noMatchHint', 'Try a different class name.')}
              />
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {filteredClasses.map((c) => {
                // Live class total = sum of section badge counts (all sections this year).
                // Falls back to the backend class count until sections finish loading, or
                // when the class has no sections (to include any section-less enrollments).
                const _secs = sectionsByClass[c.id] || [];
                const _sectionSum = _secs.reduce((a, s) => a + (s.student_count || 0), 0);
                const classTotal = _secs.length ? _sectionSum : (c.student_count || 0);
                return (
            <Card key={c.id} className="p-5" >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md flex items-center justify-center accent-classes" style={{ opacity: 0.9 }}><School className="w-5 h-5 text-white" /></div>
                  <div>
                    <p className="font-semibold">{c.name}</p>
                    <p className="text-xs text-muted-foreground" data-testid={`class-total-${c.id}`}>{classTotal} {t('dash.students')} &middot; {c.section_count || 0} {t('classes.sections')}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => { setSectionDialog(c); setSectionForm({ name: '', capacity: 40, class_teacher_id: '' }); }} data-testid={`add-section-${c.id}`}><Plus className="w-3.5 h-3.5 mr-1" /> {t('classes.newSection')}</Button>
                  <Button size="icon" variant="ghost" className="text-red-600" onClick={() => delClass(c.id)} data-testid={`delete-class-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground whitespace-nowrap">{t('rc.bulletinFormat', 'Bulletin Format')}</Label>
                  <Select value={c.bulletin_format || ''} onValueChange={(v) => setClassFormat(c.id, v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`class-format-${c.id}`}><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{BULLETIN_FORMATS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {(sectionsByClass[c.id] || []).map((s) => (
                  <div key={s.id} className="flex items-center justify-between bg-muted/40 rounded px-3 py-2" data-testid={`section-row-${s.id}`}>
                    <div className="flex items-center gap-2 text-sm">
                      <Layers className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{t('students.section')} {s.name}</span>
                      <Badge variant="outline" className="ml-1">{s.student_count || 0}/{s.capacity}</Badge>
                      {s.class_teacher_name && <span className="text-xs text-muted-foreground">&middot; {s.class_teacher_name}</span>}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="icon" variant="ghost"
                        className="h-7 w-7 text-muted-foreground hover:text-primary hover:bg-primary/10"
                        onClick={() => openRoster(c, s)}
                        title={t('classes.classList', 'Class list')} aria-label={t('classes.classList', 'Class list')}
                        data-testid={`section-classlist-${s.id}`}
                      >
                        <ClipboardList className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30" onClick={() => delSection(s.id)} data-testid={`delete-section-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
                {(sectionsByClass[c.id] || []).length === 0 && <p className="text-xs text-muted-foreground px-1">No sections yet</p>}
              </div>
            </Card>
              ); })}
            </div>
          )}
        </>
      )}

      <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) { setImportPreview(null); setImportFile(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="import-classes-dialog">
          <DialogHeader>
            <DialogTitle>{t('classes.importTitle', 'Import Classes & Sections')}</DialogTitle>
            <DialogDescription>
              {t('classes.importHint', 'Upload an .xlsx with columns: class_name, section_name. Existing classes/sections are matched by name and skipped (no duplicates).')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
              <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={onPickFile} className="hidden" id="class-file-input" data-testid="class-file-input" />
              <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="class-choose-file-button">
                {importFile ? importFile.name : t('classes.chooseFile', 'Choose Excel file')}
              </Button>
            </div>

            {importPreview && (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-4 text-sm">
                  <span><strong>{importPreview.total_rows}</strong> {t('classes.rows', 'rows')}</span>
                  <span className="text-emerald-600"><strong>{importPreview.new_classes}</strong> {t('classes.newClasses', 'new classes')}</span>
                  <span className="text-emerald-600"><strong>{importPreview.new_sections}</strong> {t('classes.newSections', 'new sections')}</span>
                  <span className="text-muted-foreground"><strong>{importPreview.existing_sections}</strong> {t('classes.existingSections', 'already exist')}</span>
                </div>
                <div className="max-h-60 overflow-y-auto border border-border rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50 sticky top-0">
                      <tr>
                        <th className="px-2 py-1 text-left">{t('classes.className', 'Class')}</th>
                        <th className="px-2 py-1 text-left">{t('classes.sectionName', 'Section')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.preview_data.map((r, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="px-2 py-1 font-medium">{r.class_name}</td>
                          <td className="px-2 py-1">{r.section_name || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {importPreview.errors?.length > 0 && (
                  <p className="text-xs text-red-600">{importPreview.errors.slice(0, 3).join('; ')}</p>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)} disabled={importing}>{t('common.cancel')}</Button>
            <Button onClick={runImport} disabled={!importPreview || importing} data-testid="class-run-import-button">
              {importing ? t('common.importing', 'Importing...') : t('classes.confirmImport', 'Confirm Import')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={classDialog} onOpenChange={setClassDialog}>
        <DialogContent data-testid="class-dialog">
          <DialogHeader><DialogTitle>{t('classes.newClass')}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>{t('common.name')} *</Label><Input value={classForm.name} onChange={(e) => setClassForm({ ...classForm, name: e.target.value })} placeholder="Grade 7" data-testid="class-name-input" /></div>
            <div><Label>{t('classes.level')}</Label><Input type="number" value={classForm.numeric_level} onChange={(e) => setClassForm({ ...classForm, numeric_level: e.target.value })} placeholder="7" /></div>
            <div>
              <Label>{t('rc.bulletinFormat', 'Bulletin Format')}</Label>
              <Select value={classForm.bulletin_format} onValueChange={(v) => setClassForm({ ...classForm, bulletin_format: v })}>
                <SelectTrigger data-testid="class-format-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{BULLETIN_FORMATS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setClassDialog(false)}>{t('common.cancel')}</Button><Button onClick={saveClass} disabled={saving} data-testid="class-save-button">{saving ? t('common.saving') : t('common.save')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sectionDialog} onOpenChange={(o) => !o && setSectionDialog(null)}>
        <DialogContent data-testid="section-dialog">
          <DialogHeader><DialogTitle>{t('classes.newSection')} &mdash; {sectionDialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>{t('common.name')} *</Label><Input value={sectionForm.name} onChange={(e) => setSectionForm({ ...sectionForm, name: e.target.value })} placeholder="A" data-testid="section-name-input" /></div>
            <div><Label>{t('classes.capacity')}</Label><Input type="number" value={sectionForm.capacity} onChange={(e) => setSectionForm({ ...sectionForm, capacity: e.target.value })} /></div>
            <div>
              <Label>{t('classes.classTeacher')}</Label>
              <Select value={sectionForm.class_teacher_id || ''} onValueChange={(v) => setSectionForm({ ...sectionForm, class_teacher_id: v })}>
                <SelectTrigger><SelectValue placeholder="-" /></SelectTrigger>
                <SelectContent>{teachers.map((tch) => <SelectItem key={tch.id} value={tch.id}>{tch.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setSectionDialog(null)}>{t('common.cancel')}</Button><Button onClick={saveSection} disabled={saving} data-testid="section-save-button">{saving ? t('common.saving') : t('common.save')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Class List (roster) dialog */}
      <Dialog open={!!rosterCtx} onOpenChange={(o) => { if (!o) { setRosterCtx(null); setRosterStudents([]); } }}>
        <DialogContent className="max-w-lg" data-testid="class-list-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5" /> {t('classes.classList', 'Class List')}
            </DialogTitle>
            <DialogDescription className="flex flex-wrap gap-x-4 gap-y-1 pt-1">
              <span><span className="text-muted-foreground">{t('exams.year', 'Year')}:</span> <strong>{activeYear?.name || '—'}</strong></span>
              <span><span className="text-muted-foreground">{t('students.class', 'Class')}:</span> <strong>{rosterCtx?.cls?.name}</strong></span>
              <span><span className="text-muted-foreground">{t('students.section', 'Section')}:</span> <strong>{rosterCtx?.section?.name}</strong></span>
            </DialogDescription>
          </DialogHeader>

          {/* Export actions */}
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={printRoster} disabled={rosterLoading || rosterStudents.length === 0} className="gap-1" data-testid="class-list-print-btn">
              <Printer className="w-4 h-4" /> {t('common.print', 'Print')}
            </Button>
            <Button variant="outline" size="sm" onClick={printRoster} disabled={rosterLoading || rosterStudents.length === 0} className="gap-1" data-testid="class-list-pdf-btn">
              <FileText className="w-4 h-4" /> {t('common.pdf', 'PDF')}
            </Button>
            <Button variant="outline" size="sm" onClick={exportRosterCsv} disabled={rosterLoading || rosterStudents.length === 0} className="gap-1" data-testid="class-list-csv-btn">
              <Download className="w-4 h-4" /> {t('common.csv', 'CSV')}
            </Button>
          </div>

          <div className="max-h-[55vh] overflow-auto rounded-md border border-border">
            {rosterLoading ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : rosterStudents.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <School className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">{t('classes.noStudentsInSection', 'No students in this section')}</p>
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="class-list-table">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left w-12 text-muted-foreground font-medium">#</th>
                    <th className="px-3 py-2 text-left text-muted-foreground font-medium">{t('common.name', 'Full Name')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rosterStudents.map((s, i) => (
                    <tr key={s.id} className="border-t border-border" data-testid={`class-list-row-${s.id}`}>
                      <td className="px-3 py-2 text-muted-foreground tabular-nums">{i + 1}</td>
                      <td className="px-3 py-2 font-medium">{s.name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="text-sm font-semibold text-right" data-testid="class-list-total">
            {t('classes.totalStudents', 'Total Students')}: {rosterLoading ? '…' : rosterStudents.length}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setRosterCtx(null); setRosterStudents([]); }}>{t('common.close', 'Close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
